/**
 * Eksportet e regjistrit "Lista e Pagave" — Excel, PDF dhe printim.
 *
 * Të treja ushqehen nga i njëjti model (`buildRegisterKS`), ndaj shifra që sheh
 * në ekran, ajo që shtypet dhe ajo që dorëzohet janë e njëjta. Tri llogaritje
 * paralele do të thoshin tri të vërteta.
 *
 * ── Dizajni ──────────────────────────────────────────────────────────────────
 * Shtatëmbëdhjetë kolona të barabarta janë të palexueshme; formulari zyrtar i
 * ka pikërisht ashtu, si rrjetë e zbrazët. Këtu ato grupohen në pesë blloqe me
 * kokë dyshe — Punonjësi · Të ardhurat · Kontributet · Ndalesat · NETO — që syri
 * të kapë strukturën para se të lexojë numrat.
 *
 * Kolonat MBLIDHEN te NETO: bruto minus kontributi i punëtorit, tatimi, sigurimi,
 * avanci dhe ndalesat. Pa sigurimin e avancin, rreshti nuk dilte dot dhe askush
 * s'do ta dinte pse.
 */
// xlsx-js-style: fork i `xlsx` me të njëjtin API, që shkruan VËRTET stile.
// `xlsx` i thjeshtë i shpërfill në heshtje — provuar: ngjyra nuk del te
// styles.xml. Është varësi e projektit prej kohësh, jo shtesë e re.
import * as XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { REGISTER_COLUMNS } from './payrollRegisterKS';
import { MONTHS_SQ } from './period';

/**
 * Vizaton logon e kompanisë dhe kthen gjerësinë e zënë (0 nëse s'ka).
 *
 * Përmasat dhe formati merren nga `doc.getImageProperties`, jo nga një
 * `new Image()` me `onload`. Ajo rrugë varej nga ngarkimi asinkron i një
 * data-URI-t 26 KB: nëse `onload` nuk shkrepte — CSP, shfletues i ngarkuar,
 * ose thjesht një `catch` bosh — logoja mungonte pa asnjë shenjë. jsPDF-ja e
 * analizon vetë skedarin dhe e di edhe llojin edhe përmasat.
 *
 * `fileType` prej saj është burimi i vërtetë i formatit: te kjo bazë logoja
 * ruhet si `data:image/png;base64,/9j/…` — etiketa gënjen, bajtet jo.
 */
export function drawCompanyLogo(doc, dataUrl, x, y, maxH = 9, maxW = 34) {
  if (!dataUrl || typeof dataUrl !== 'string') return 0;
  try {
    const p = doc.getImageProperties(dataUrl);
    if (!p || !p.width || !p.height) return 0;
    const w = Math.min(maxH * (p.width / p.height), maxW);
    const h = w / (p.width / p.height);
    doc.addImage(dataUrl, p.fileType || 'JPEG', x, y, w, h);
    return w;
  } catch {
    return 0;          // pa logo, jo pa raport
  }
}


const money = (v) => Number(v || 0).toLocaleString('sq-AL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const periodText = (h) => (h.month && h.year ? `${MONTHS_SQ[h.month - 1]} ${h.year}` : String(h.year || ''));
const fileBase = (h) => `lista-e-pagave-${h.year || ''}-${String(h.month || '').padStart(2, '0')}`;

// E njëjta paletë si PDF-ja — vetëm forma ndryshon (jsPDF do varg numrash,
// xlsx do hex pa '#').
const HEX = { ink: '111827', muted: '6B7280', rule: 'E2E8F0', brand: '4893A5', navy: '303D50', soft: 'F8FAFC', zebra: 'FCFCFD' };
const INK    = [17, 24, 39];
const MUTED  = [107, 114, 128];
const RULE   = [226, 232, 240];
const BRAND  = [72, 147, 165];      // #4893A5
const NAVY   = [48, 61, 80];        // #303D50

/** Grupimi i kolonave — çelësat ndjekin REGISTER_COLUMNS, jo pozicione fikse. */
const GROUPS = [
  { label: '',                keys: ['no', 'firstName', 'lastName', 'id', 'secondJob', 'residency'] },
  { label: 'TË ARDHURAT',     keys: ['base', 'overtime', 'bonus', 'gross'] },
  { label: 'KONTRIBUTET',     keys: ['contribEmployee', 'contribEmployer'] },
  { label: 'NDALESAT',        keys: ['taxTotal', 'health', 'advance', 'penalty'] },
  { label: '',                keys: ['net'] },
];

/** Etiketa të shkurtra: koka dyshe e mban kontekstin, ndaj teksti i gjatë hiqet. */
const SHORT = {
  no: 'Nr.', firstName: 'Emri', lastName: 'Mbiemri', id: 'ID', secondJob: 'Punësimi', residency: 'Rezidenca',
  base: 'Bazë', overtime: 'Orë shtesë', bonus: 'Bonus', gross: 'Bruto',
  contribEmployee: 'Punëtori', contribEmployer: 'Punëdhënësi',
  taxTotal: 'Tatimi', health: 'Sigurimi', advance: 'Avanci', penalty: 'Ndalesa',
  net: 'NETO',
};

const cellValue = (row, col) => {
  const v = row[col.key];
  if (col.money) return Number(v) || 0;
  if (col.pct)   return `${Number(v) || 0}%`;
  return v ?? '';
};

const TOTAL_KEYS = ['base', 'overtime', 'bonus', 'gross',
                    'contribEmployee', 'contribEmployer',
                    'taxTotal', 'health', 'advance', 'penalty', 'net'];

/**
 * Përshkrimi i vetëm i faqosjes — çka shkruhet, në ç'rend, me ç'etiketa.
 *
 * Excel-i dhe PDF-ja e lexojnë KËTË, jo secili të vetin. Më parë koka e njërës
 * kishte katër rreshta dhe e tjetrës tre, me etiketa të ndryshme; dy raporte të
 * të njëjtës pagë që dukeshin si dy dokumente. Çdo ndryshim këtu del në të dyja.
 *
 * Formatimi mbetet i secilës: Excel-i s'mban ngjyra e logo, PDF-ja s'mban
 * formula. Por përmbajtja dhe rendi janë të përbashkëta.
 */
export function registerLayout(reg) {
  const h = reg.header;
  const st = reg.stats;
  return {
    title: 'LISTA E PAGAVE',
    // Blloku i majtë — kush e lëshon listën.
    left: [
      h.companyName || '',
      [h.nrb && `NRB ${h.nrb}`, h.nrf && `NRF ${h.nrf}`].filter(Boolean).join('   ·   '),
      h.address || '',
      h.phone ? `Tel: ${h.phone}` : '',
    ],
    // Blloku i djathtë — për cilën periudhë dhe në cilën llogari.
    right: [
      `Periudha: ${periodText(h)}`,
      `Punonjës: ${reg.rows.length}`,
      h.bank ? `Banka: ${h.bank}` : '',
      h.account ? `Xhirollogaria: ${h.account}` : '',
    ],
    analysis: [
      { label: 'Paga më e ulët',       value: st.min },
      { label: 'Paga mesatare',        value: st.avg },
      { label: 'Paga më e lartë',      value: st.max },
      { label: 'Ndalesat gjithsej',    value: st.deductions },
      { label: 'Kosto e punëdhënësit', value: st.employerCost },
    ],
    analysisTitle: 'ANALIZA E PAGAVE',
    totalLabel: 'TOTALI',
    signature: {
      left:  'Data: ______________',
      right: 'Nënshkrimi i punëdhënësit / Personit të autorizuar',
      director: h.director || '',
    },
    footer: [h.companyName, 'Lista e Pagave', periodText(h)].filter(Boolean).join(' · '),
  };
}

// ── Excel ───────────────────────────────────────────────────────────────────
/**
 * Ndërton librin e punës. I ndarë nga shkrimi në disk që të jetë i testueshëm:
 * përndryshe e vetmja mënyrë për të parë çka del do të ishte hapja e skedarit.
 */
export function buildRegisterWorkbook(reg) {
  const cols = REGISTER_COLUMNS;
  const N = cols.length;
  const merges = [];
  const M = (r, c1, c2) => merges.push({ s: { r, c: c1 }, e: { r, c: c2 } });

  // Koka e kompanisë shkruhet në qeliza të BASHKUARA, jo në kolonën A.
  // Kolona A është 5 shkronja e gjerë (mban "Nr."), ndaj "EverLeaf SH.P.K."
  // dhe "Xhirollogaria: 1234567890123456" dilnin të prera ose mbi njëra-tjetrën.
  const LEFT_TO = 5;                 // A:F për bllokun e majtë
  const RIGHT_FROM = N - 6;          // gjashtë kolonat e fundit për të djathtin

  const L = registerLayout(reg);
  const { left, right } = L;

  const blank = () => Array(N).fill('');
  const aoa = [];
  const put = (row, col, val) => { row[col] = val; };

  // 0 — titulli, i bashkuar mbi tërë gjerësinë
  const title = blank(); put(title, 0, L.title); aoa.push(title);
  M(0, 0, N - 1);

  // 1..4 — kompania majtas, periudha e banka djathtas
  for (let i = 0; i < 4; i++) {
    const row = blank();
    put(row, 0, left[i]);
    put(row, RIGHT_FROM, right[i]);
    aoa.push(row);
    M(aoa.length - 1, 0, LEFT_TO);
    M(aoa.length - 1, RIGHT_FROM, N - 1);
  }

  aoa.push(blank());                                   // 5 — frymëmarrje

  // 6 — grupet, të bashkuara mbi kolonat e veta
  const groupRow = blank();
  let c = 0;
  GROUPS.forEach((g) => {
    if (g.label) {
      put(groupRow, c, g.label);
      if (g.keys.length > 1) M(aoa.length, c, c + g.keys.length - 1);
    }
    c += g.keys.length;
  });
  aoa.push(groupRow);
  const GROUP_ROW = aoa.length - 1;

  // 7 — kokat e kolonave
  aoa.push(cols.map((x) => SHORT[x.key] || x.label));
  const HEAD_ROWS = aoa.length;                        // rreshtat para të dhënave

  // 8.. — rreshtat
  reg.rows.forEach((r) => aoa.push(cols.map((x) => cellValue(r, x))));

  // TOTALI
  const totalRow = blank();
  put(totalRow, 1, L.totalLabel);
  TOTAL_KEYS.forEach((k) => {
    const i = cols.findIndex((x) => x.key === k);
    if (i >= 0) totalRow[i] = reg.totals[k];
  });
  aoa.push(totalRow);
  const TOTAL_ROW = aoa.length - 1;

  aoa.push(blank());

  // Analiza — etiketa mbi vlerë, secila e bashkuar në dy kolona që të hyjë teksti
  const analysis = L.analysis.map((a) => [a.label, a.value]);
  const aTitle = blank(); put(aTitle, 0, L.analysisTitle); aoa.push(aTitle);
  const A_TITLE = aoa.length - 1;
  M(aoa.length - 1, 0, 3);

  const aLabels = blank(); const aValues = blank();
  analysis.forEach(([k, v], i) => { put(aLabels, i * 2, k); put(aValues, i * 2, v); });
  aoa.push(aLabels); const A_LABELS = aoa.length - 1;
  aoa.push(aValues); const A_VALUES = aoa.length - 1;
  analysis.forEach((_, i) => { M(A_LABELS, i * 2, i * 2 + 1); M(A_VALUES, i * 2, i * 2 + 1); });

  aoa.push(blank());
  const sign = blank();
  put(sign, 0, L.signature.left);
  put(sign, RIGHT_FROM, L.signature.right);
  aoa.push(sign);
  const SIGN_ROW = aoa.length - 1;
  M(SIGN_ROW, 0, 3);
  M(SIGN_ROW, RIGHT_FROM, N - 1);

  aoa.push(blank());
  const foot = blank(); put(foot, 0, L.footer); aoa.push(foot);
  const FOOT_ROW = aoa.length - 1;
  M(FOOT_ROW, 0, Math.min(N - 1, 7));

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = cols.map((x) => ({ wch: x.w }));
  ws['!merges'] = merges;
  ws['!rows'] = [{ hpt: 22 }];                      // titulli merr frymë

  // ── Stilet ────────────────────────────────────────────────────────────────
  // E njëjta hierarki si te PDF-ja: titull navy, kokë e çelët me vijë poshtë,
  // rreshta me vijë të hollë, TOTALI me vijë teal sipër. Pa këto, skedari del
  // mur shifrash — pikërisht ndryshimi mes tij dhe PDF-së.
  const at = (r, c) => ws[XLSX.utils.encode_cell({ r, c })];
  const style = (r, c, st) => { const cell = at(r, c); if (cell) cell.s = { ...(cell.s || {}), ...st }; };
  const styleRow = (r, st) => { for (let c = 0; c < N; c++) style(r, c, st); };
  const line = (color, side = 'bottom', w = 'thin') => ({ [side]: { style: w, color: { rgb: color } } });

  styleRow(0, { font: { bold: true, sz: 14, color: { rgb: HEX.navy } },
                alignment: { vertical: 'center' } });
  for (let r = 1; r <= 4; r++) {
    style(r, 0, { font: { sz: 9, bold: r === 1, color: { rgb: r === 1 ? HEX.ink : HEX.muted } } });
    style(r, RIGHT_FROM, { font: { sz: 9, color: { rgb: HEX.muted } }, alignment: { horizontal: 'right' } });
  }
  // Vija teal që ndan kokën nga tabela — si te PDF-ja.
  styleRow(5, { border: line(HEX.brand, 'bottom', 'medium') });

  styleRow(GROUP_ROW, {
    font: { bold: true, sz: 8, color: { rgb: HEX.muted } },
    alignment: { horizontal: 'center' },
    fill: { fgColor: { rgb: 'FFFFFF' } },
  });
  styleRow(HEAD_ROWS - 1, {
    font: { bold: true, sz: 9, color: { rgb: HEX.ink } },
    fill: { fgColor: { rgb: HEX.soft } },
    border: line(HEX.rule),
    alignment: { horizontal: 'center', wrapText: true },
  });

  for (let r = HEAD_ROWS; r < TOTAL_ROW; r++) {
    const zebra = (r - HEAD_ROWS) % 2 === 1;
    for (let c = 0; c < N; c++) {
      const col = cols[c];
      style(r, c, {
        font: { sz: 9, bold: col.key === 'net', color: { rgb: HEX.ink } },
        alignment: { horizontal: col.money ? 'right' : (col.key === 'no' ? 'center' : 'left') },
        border: line('F1F5F9'),
        ...(zebra ? { fill: { fgColor: { rgb: HEX.zebra } } } : {}),
      });
    }
  }

  styleRow(TOTAL_ROW, {
    font: { bold: true, sz: 9.5, color: { rgb: HEX.navy } },
    border: line(HEX.brand, 'top', 'medium'),
  });
  cols.forEach((col, c) => { if (col.money) style(TOTAL_ROW, c, { alignment: { horizontal: 'right' } }); });

  style(A_TITLE, 0, { font: { bold: true, sz: 9, color: { rgb: HEX.muted } } });
  L.analysis.forEach((_, i) => {
    const c = i * 2;
    const last = i === L.analysis.length - 1;      // kostoja — e theksuar si te PDF-ja
    style(A_LABELS, c, {
      font: { bold: true, sz: 8, color: { rgb: last ? 'FFFFFF' : HEX.muted } },
      fill: { fgColor: { rgb: last ? HEX.navy : HEX.soft } },
      alignment: { horizontal: 'center' },
      border: line(HEX.rule, 'top'),
    });
    style(A_VALUES, c, {
      font: { bold: true, sz: 12, color: { rgb: last ? 'FFFFFF' : HEX.ink } },
      fill: { fgColor: { rgb: last ? HEX.navy : HEX.soft } },
      alignment: { horizontal: 'center' },
      border: line(HEX.rule),
    });
  });

  style(SIGN_ROW, 0, { font: { sz: 9, color: { rgb: HEX.muted } }, border: line(HEX.rule, 'top') });
  style(SIGN_ROW, RIGHT_FROM, { font: { sz: 9, color: { rgb: HEX.muted } }, alignment: { horizontal: 'right' }, border: line(HEX.rule, 'top') });
  style(FOOT_ROW, 0, { font: { sz: 8, italic: true, color: { rgb: HEX.muted } } });

  // Formati i numrave. `xlsx` në versionin e lirë nuk mban fonte apo ngjyra —
  // as panele të ngrira, provuar: shkruan `<sheetView>` pa `<pane>` sido që t'i
  // vendosësh. Por `z` po, dhe pikërisht ai e ndan një tabelë të lexueshme nga
  // një mur shifrash: `1234.5` bëhet `1,234.50`.
  const MONEY = '#,##0.00';
  const moneyCols = cols.map((x, i) => (x.money ? i : -1)).filter((i) => i >= 0);
  for (let r = HEAD_ROWS; r <= TOTAL_ROW; r++) {
    moneyCols.forEach((cc) => {
      const ref = XLSX.utils.encode_cell({ r, c: cc });
      if (ws[ref] && typeof ws[ref].v === 'number') ws[ref].z = MONEY;
    });
  }
  analysis.forEach((_, i) => {
    const ref = XLSX.utils.encode_cell({ r: A_VALUES, c: i * 2 });
    if (ws[ref] && typeof ws[ref].v === 'number') ws[ref].z = MONEY;
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Lista e Pagave');
  return wb;
}

export function exportRegisterExcel(reg) {
  XLSX.writeFile(buildRegisterWorkbook(reg), `${fileBase(reg.header)}.xlsx`);
}

// ── PDF ─────────────────────────────────────────────────────────────────────
export async function buildRegisterPdf(reg) {
  const h = reg.header;
  const L = registerLayout(reg);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const PW = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight();
  const M = 10;

  // Logoja është e KOMPANISË, jo e Nexuma-s: ky raport nënshkruhet nga
  // punëdhënësi dhe dorëzohet në emrin e tij. Pa logo, mbetet vetëm emri.
  // ── Koka: kompania majtas, periudha djathtas, një vijë e vetme mes tyre ────
  const logoW = drawCompanyLogo(doc, h.logo, M, 9.5, 10, 36);
  const titleX = M + (logoW ? logoW + 4 : 0);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...NAVY);
  doc.text('Lista e Pagave', titleX, 16.5);

  // Të njëjtat rreshta si te Excel-i, nga i njëjti përshkrim — vetëm formatimi
  // ndryshon: emri i kompanisë me theks, pjesa tjetër e qetë.
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...INK);
  doc.text(L.left[0] || '—', M, 24);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...MUTED);
  L.left.slice(1).filter(Boolean).forEach((l, i) => doc.text(l, M, 28.5 + i * 3.6));
  L.right.filter(Boolean).forEach((l, i) => doc.text(l, PW - M, 24 + i * 3.6, { align: 'right' }));

  doc.setDrawColor(...BRAND); doc.setLineWidth(0.5);
  doc.line(M, 38, PW - M, 38);

  // ── Tabela: kokë dyshe, pesë blloqe ───────────────────────────────────────
  const cols = REGISTER_COLUMNS;
  const groupHead = GROUPS.map((g) => ({
    content: g.label,
    colSpan: g.keys.length,
    styles: { halign: 'center', fillColor: [255, 255, 255], textColor: MUTED, fontSize: 5.4, fontStyle: 'bold' },
  }));
  const colHead = cols.map((x) => ({
    content: SHORT[x.key] || x.label,
    styles: { halign: x.money || x.pct ? 'right' : (x.key === 'no' ? 'center' : 'left') },
  }));
  const totalRow = cols.map((x, i) => {
    if (i === 1) return { content: L.totalLabel, styles: { halign: 'left' } };
    return TOTAL_KEYS.includes(x.key) ? money(reg.totals[x.key]) : '';
  });

  autoTable(doc, {
    head: [groupHead, colHead],
    body: reg.rows.map((r) => cols.map((x) => (x.money ? money(r[x.key]) : String(cellValue(r, x))))),
    foot: [totalRow],
    startY: 42,
    margin: { left: M, right: M, bottom: 20 },
    theme: 'plain',                       // rrjeta e plotë i mbyt numrat
    styles: { fontSize: 6, cellPadding: { top: 1.3, bottom: 1.3, left: 1.2, right: 1.2 }, textColor: INK, overflow: 'linebreak' },
    headStyles: { fillColor: [248, 250, 252], textColor: INK, fontSize: 5.6, fontStyle: 'bold', lineColor: RULE, lineWidth: { bottom: 0.3 } },
    footStyles: { fillColor: [255, 255, 255], textColor: NAVY, fontSize: 6.4, fontStyle: 'bold', lineColor: BRAND, lineWidth: { top: 0.5 } },
    // Vija vetëm mes rreshtave: struktura vjen nga bardhësia, jo nga korniza.
    bodyStyles: { lineColor: [241, 245, 249], lineWidth: { bottom: 0.1 } },
    columnStyles: Object.fromEntries(cols.map((x, i) => [i, {
      halign: x.money || x.pct ? 'right' : (x.key === 'no' ? 'center' : 'left'),
      textColor: x.pct ? MUTED : INK,
      fontStyle: x.key === 'net' ? 'bold' : 'normal',
    }])),
    didParseCell: (d) => {
      // Ndarësit mes blloqeve: një vijë e hollë vertikale, jo rrjetë e plotë.
      const starts = [];
      let acc = 0;
      GROUPS.forEach((g) => { starts.push(acc); acc += g.keys.length; });
      if (starts.includes(d.column.index) && d.column.index !== 0) {
        d.cell.styles.lineWidth = { ...(d.cell.styles.lineWidth || {}), left: 0.1 };
        d.cell.styles.lineColor = RULE;
      }
    },
  });

  // ── Klasifikimi: shirit kompakt, jo tabelë e dytë ─────────────────────────
  let y = (doc.lastAutoTable?.finalY || 60) + 7;
  if (y > PH - 26) { doc.addPage(); y = 20; }

  doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor(...MUTED);
  doc.text(L.analysisTitle, M, y);
  y += 3;

  // Mesatarja pranë medianës me qëllim: kur largohen nga njëra-tjetra, një pagë
  // e vetme e lartë po e tërheq mesataren mbi atë që merr shumica.
  // Katër të parat si kuti të qeta; e fundit — kostoja — veçohet, sepse është
  // e vetmja shifër që nuk del askund tjetër te lista.
  const cards = L.analysis.slice(0, -1).map((a) => ({ k: a.label, v: `${money(a.value)} €` }));
  const highlight = L.analysis[L.analysis.length - 1];
  const bw = 34, gap = 2.5;
  cards.forEach((c, i) => {
    const x = M + i * (bw + gap);
    doc.setDrawColor(...RULE); doc.setLineWidth(0.1);
    doc.roundedRect(x, y, bw, 11, 1, 1, 'S');
    doc.setFont('helvetica', 'normal'); doc.setFontSize(5.2); doc.setTextColor(...MUTED);
    doc.text(c.k.toUpperCase(), x + 2.5, y + 3.8);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...INK);
    doc.text(c.v, x + 2.5, y + 8.6);
  });

  // Kostoja e punëdhënësit veçohet: është e vetmja shifër këtu që nuk del
  // askund tjetër te lista, dhe është ajo që del vërtet nga arka.
  const cx = M + cards.length * (bw + gap);
  const cw = 46;
  doc.setFillColor(...NAVY);
  doc.roundedRect(cx, y, cw, 11, 1, 1, 'F');
  doc.setFont('helvetica', 'normal'); doc.setFontSize(5.2); doc.setTextColor(255, 255, 255);
  doc.text(highlight.label.toUpperCase(), cx + 3, y + 3.8);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5);
  doc.text(`${money(highlight.value)} €`, cx + 3, y + 8.8);

  // ── Nënshkrimi ────────────────────────────────────────────────────────────
  const sy = y + 22;
  doc.setDrawColor(...RULE); doc.setLineWidth(0.2);
  doc.line(PW - M - 70, sy, PW - M, sy);
  doc.line(M, sy, M + 45, sy);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(...MUTED);
  doc.text(L.signature.left.replace(/:.*$/, ''), M, sy + 3.5);
  doc.text(L.signature.right, PW - M, sy + 3.5, { align: 'right' });

  // Fundi i faqes në çdo faqe.
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFontSize(5.6); doc.setTextColor(...MUTED);
    doc.text(L.footer, M, PH - 7);
    doc.text(`${p} / ${pages}`, PW - M, PH - 7, { align: 'right' });
  }
  return doc;
}

export async function downloadRegisterPdf(reg) {
  (await buildRegisterPdf(reg)).save(`${fileBase(reg.header)}.pdf`);
}

// ── Printim ─────────────────────────────────────────────────────────────────
/**
 * Printimi ndan të njëjtin PDF si shkarkimi — jo një HTML të dytë. Një pamje
 * printimi e ndërtuar veç do të dilte ndryshe nga skedari që dorëzohet, dhe
 * dallimi do të zbulohej vetëm mbi letër.
 */
export async function printRegister(reg) {
  const url = (await buildRegisterPdf(reg)).output('bloburl');
  const w = window.open(url, '_blank');
  if (!w) return false;                       // bllokuar nga shfletuesi
  w.addEventListener('load', () => { try { w.focus(); w.print(); } catch { /* përdoruesi printon vetë */ } });
  return true;
}
